import AccountPicker from "../../components/AccountPicker";
import { useMemo, useState } from "react";
import { Shield } from "lucide-react";
import type { Account, GoalStatus, Owner, SavingGoal } from "../../types";
import { api } from "../../lib/api";
import { resolveGoalCushionAccountId } from "../../lib/goal-reserves";
import DateInput from "../../components/DateInput";
import FormActions from "../../components/FormActions";
import Modal from "../../components/Modal";
import GoalScheduleFields, { type GoalScheduleDraft } from "./GoalScheduleFields";

export interface GoalFormProps {
  goal?: SavingGoal | null;
  accounts: Account[];
  profiles: Owner[];
  baseCurrency: string;
  today: string;
  defaultProfile?: string;
  existingPools?: string[];
  onClose: () => void;
  onSaved: () => void;
}

export default function GoalForm({
  goal,
  accounts,
  profiles,
  baseCurrency,
  today,
  defaultProfile,
  existingPools,
  onClose,
  onSaved,
}: GoalFormProps) {
  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.active !== false),
    [accounts],
  );

  const availableCurrencies = useMemo(() => {
    return [
      ...new Set([
        baseCurrency || "MVR",
        "USD",
        "EUR",
        "GBP",
        ...accounts.map((a) => a.currency).filter(Boolean),
      ]),
    ];
  }, [baseCurrency, accounts]);

  const [name, setName] = useState(goal?.name ?? "");
  const [targetAmount, setTargetAmount] = useState(goal ? String(goal.targetAmount) : "");
  const [currency, setCurrency] = useState(goal?.currency ?? baseCurrency ?? "MVR");
  const [profile, setProfile] = useState<string>(goal?.profile ?? defaultProfile ?? "");
  const [targetDate, setTargetDate] = useState(goal?.targetDate ?? "");
  const [floatingGroup, setFloatingGroup] = useState(goal?.floatingGroup ?? "");
  const [cushionAmount, setCushionAmount] = useState(goal?.cushionAmount ? String(goal.cushionAmount) : "");
  const [cushionAccountId, setCushionAccountId] = useState(goal?.cushionAccountId ?? "");
  const [notes, setNotes] = useState(goal?.notes ?? "");
  const [status, setStatus] = useState<GoalStatus>(goal?.status ?? "active");
  const [initialContribution, setInitialContribution] = useState(!goal);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [schedules, setSchedules] = useState<GoalScheduleDraft[]>(() => {
    if (goal?.schedules && goal.schedules.length > 0) {
      return goal.schedules.map((s) => ({
        profile: s.profile ?? "",
        accountId: s.accountId,
        monthlyAmount: String(s.monthlyAmount),
      }));
    }
    return [];
  });

  function addSchedule() {
    const preferredId = resolveGoalCushionAccountId(
      { cushionAccountId: cushionAccountId || null, profile: profile || null, currency },
      accounts,
    );
    const firstAcc =
      accounts.find((a) => a.id === preferredId) ?? activeAccounts[0] ?? accounts[0];
    if (!firstAcc) return;
    setSchedules([
      ...schedules,
      { profile: profile || (profiles[0] ?? ""), accountId: firstAcc.id, monthlyAmount: "" },
    ]);
  }

  function removeSchedule(index: number) {
    setSchedules(schedules.filter((_, i) => i !== index));
  }

  function updateSchedule(index: number, field: keyof GoalScheduleDraft, value: string) {
    const updated = [...schedules];
    updated[index] = { ...updated[index], [field]: value };
    setSchedules(updated);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Enter a goal name.");
      return;
    }
    if (!targetAmount.trim() || Number(targetAmount) <= 0) {
      setError("Enter a valid target amount.");
      return;
    }

    setSaving(true);
    setError("");

    const validSchedules = schedules
      .filter((s) => s.accountId && Number(s.monthlyAmount) > 0)
      .map((s) => ({
        profile: s.profile || null,
        accountId: s.accountId,
        monthlyAmount: s.monthlyAmount,
      }));

    const resolvedCushionAccountId =
      cushionAccountId ||
      (cushionAmount.trim() && Number(cushionAmount) > 0
        ? resolveGoalCushionAccountId(
            {
              profile: profile || null,
              currency,
              schedules: validSchedules,
              contributions: goal?.contributions,
            },
            accounts,
          )
        : null);

    try {
      await api("v2/goals/save", {
        id: goal?.id,
        startDate: goal?.startDate ?? today,
        expectedUpdatedAt: goal?.updatedAt,
        name: name.trim(),
        profile: profile ? profile : null,
        targetAmount,
        currency,
        category: "general",
        targetDate: targetDate || null,
        floatingGroup: floatingGroup.trim() || null,
        color: "mint",
        notes: notes.trim() || null,
        cushionAmount: cushionAmount.trim() ? cushionAmount.trim() : null,
        cushionAccountId: resolvedCushionAccountId || null,
        status,
        schedules: validSchedules,
        initialContribution: !goal && initialContribution && validSchedules.length > 0,
      });

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save goal.");
      setSaving(false);
    }
  }

  return (
    <Modal title={goal ? "Edit savings goal" : "New savings goal"} onClose={onClose}>
      <form onSubmit={handleSubmit} className="goal-form">
        {error && <div className="form-error" role="alert">{error}</div>}

        <label>
          <span>Goal name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. M1 Mac mini, Trip to Japan, Emergency buffer"
            required
            autoFocus
          />
        </label>

        <div className="goal-form-amount-grid">
          <label>
            <span>Target amount</span>
            <input
              type="text"
              inputMode="decimal"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              placeholder="e.g. 15000"
              required
            />
          </label>
          <label>
            <span>Currency</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {availableCurrencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          <span>Owner</span>
          <select
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
          >
            <option value="">Household (Shared)</option>
            {profiles.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>

        <div className="goal-form-grid-2">
          <label>
            <span className="field-label">Target date <small>Optional</small></span>
            <DateInput
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </label>
          <label>
            <span className="field-label">Shared pool <small>Optional</small></span>
            <input
              type="text"
              list="goal-floating-pools"
              value={floatingGroup}
              onChange={(e) => setFloatingGroup(e.target.value)}
              placeholder="e.g. Wishlist"
            />
            {existingPools && existingPools.length > 0 && (
              <datalist id="goal-floating-pools">
                {existingPools.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            )}
          </label>
        </div>

        {goal && (
          <label>
            <span>Goal status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as GoalStatus)}
            >
              <option value="active" disabled={!!goal.completedDate}>Active</option>
              <option value="paused" disabled={!!goal.completedDate}>Paused</option>
              <option value="achieved">Achieved (release unused savings)</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        )}

        <label>
          <span className="field-label">Notes <small>Optional</small></span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Details or notes about this goal"
          />
        </label>

        <div className="goal-form-section">
          <div className="goal-form-section-head">
            <span className="goal-form-section-title">
              <Shield size={14} style={{ color: "var(--mint)" }} />
              Account cushion / Safety buffer
            </span>
            <span className="goal-form-optional-tag">Optional</span>
          </div>
          <p className="goal-form-section-desc">
            Hold purchase until your account has enough balance left after buying (e.g. keep at least 5,000 in your account).
          </p>
          <div className="goal-form-grid-2">
            <label>
              <span>Minimum cushion ({currency})</span>
              <input
                type="text"
                inputMode="decimal"
                value={cushionAmount}
                onChange={(e) => setCushionAmount(e.target.value)}
                placeholder="e.g. 5000"
              />
            </label>
            <AccountPicker label="Account to protect" accounts={accounts.filter(a => a.active !== false || a.id === cushionAccountId)} value={cushionAccountId} onChange={(id) => setCushionAccountId(id)} emptyOption={{ value: "", label: "Auto (Primary)" }} />
          </div>
        </div>

        <GoalScheduleFields schedules={schedules} accounts={accounts} profiles={profiles} currency={currency}
          addSchedule={addSchedule} removeSchedule={removeSchedule} updateSchedule={updateSchedule} />

        {!goal && schedules.length > 0 && (
          <label className="goal-check-row">
            <input
              type="checkbox"
              checked={initialContribution}
              onChange={(e) => setInitialContribution(e.target.checked)}
            />
            <span>Record first month's contribution today ({today})</span>
          </label>
        )}

        {goal && status === "achieved" && !goal.completedDate && <p className="muted">Saving will finish this goal, release unused savings and stop future monthly saving. Your history will be preserved.</p>}
        <FormActions label={goal ? "Save goal" : "Create goal"} busy={saving} />
      </form>
    </Modal>
  );
}
